import * as THREE from 'three';
import { CharacterModel } from './CharacterModel';
import type { Collider } from '../physics/Collision';
import { resolveCircle } from '../physics/Collision';
import { FLAME_Y, LAMP_LIGHT, lampBody } from '../effects/Particles';
import { nameSprite, speechSprite } from '../effects/textures';

/**
 * Lão Đăng – người soi đường: bạn đồng hành cầm đèn, luôn đi trước – chếch về bên trái người chơi một chút
 * (tay phải cầm đèn hướng vào giữa) để ánh đèn rọi xuống con đường phía trước.
 * - Đổi kiểu đèn (dầu / lồng giấy / hoa sen / lưu ly) – PointLight giữ nguyên để không biên dịch lại shader.
 * - "Cảm" vật ẩn: `sense` 0..1 (WorldManager đặt theo khoảng cách tới mảnh ký ức gần nhất) ⇒ đèn chập chờn, sáng hơn.
 * - Nói (bong bóng chữ trên đầu) và chỉ đường (quay về hướng mục tiêu, giơ tay).
 * Chỉ hiển thị ở client (không đồng bộ multiplayer). Không chặn người chơi, tự né vật cản; bị bỏ xa thì "hiện" lại.
 */
export class LampBearer {
  readonly model: CharacterModel;
  private readonly anchor = new THREE.Group(); // gắn vào bàn tay, giữ thẳng đứng
  private body: ReturnType<typeof lampBody>;
  readonly light: THREE.PointLight;
  private conf = LAMP_LIGHT.dau;
  style = 'dau';
  readonly position = new THREE.Vector3();
  private rotY = Math.PI;
  private velocity = new THREE.Vector3();
  private heightAt: (x: number, z: number) => number = () => 0;
  private colliders: Collider[] = [];
  private enabled = true;
  private hidden = false;
  private fade = 1;
  private readonly q1 = new THREE.Quaternion(); private readonly q2 = new THREE.Quaternion();
  private t = 0;
  /** 0..1 – độ gần vật ẩn */
  sense = 0;
  private bubble?: THREE.Sprite; private bubbleUntil = 0;
  private pointing: { x: number; z: number; until: number } | null = null;
  /** Đứng yên để người chơi trò chuyện */
  holdStill = false;

  constructor(parent: THREE.Object3D, shadows: boolean) {
    this.model = new CharacterModel({
      appearance: { hairStyle: 'ngan', hairColor: '#cfc6bc', face: 'hien', skinTone: '#c49a74', outfit: 'ao_nau', accessory: 'khong', aura: 'khong' },
      gender: 'nam', variant: 'lamp_bearer',
    });
    this.model.holdForward = true;
    this.model.root.traverse((o) => { if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = shadows; });
    this.anchor.position.set(0, -0.55, 0.02); // treo dưới bàn tay phải
    this.model.rightHand.add(this.anchor);
    this.light = new THREE.PointLight(this.conf.color, this.conf.intensity, this.conf.distance, 1.25); this.light.position.y = FLAME_Y; this.light.castShadow = false;
    this.anchor.add(this.light);
    this.body = lampBody('dau'); this.anchor.add(this.body.group);
    const tag = nameSprite('Lão Đăng', 'Người soi đường', '#ffd9a0'); tag.position.y = 2.2; tag.scale.multiplyScalar(0.8); this.model.root.add(tag);
    parent.add(this.model.root);
  }

  /** Đổi kiểu đèn: thay thân đèn, chỉnh màu/cường độ ánh sáng (cùng một PointLight). */
  setStyle(style: string) {
    if (style === this.style && this.body) return;
    this.style = LAMP_LIGHT[style] ? style : 'dau'; this.conf = LAMP_LIGHT[this.style];
    this.body.group.traverse((o) => { const m = o as THREE.Mesh; m.geometry?.dispose(); if (m.material && !Array.isArray(m.material)) (m.material as THREE.Material).dispose(); });
    this.body.group.removeFromParent();
    this.body = lampBody(this.style); this.anchor.add(this.body.group);
    this.light.color.set(this.conf.color); this.light.distance = this.conf.distance;
  }

  setWorld(heightAt: (x: number, z: number) => number, colliders: Collider[]) { this.heightAt = heightAt; this.colliders = colliders; }
  setEnabled(on: boolean) { this.enabled = on; if (!on) this.clearBubble(); }
  get isEnabled() { return this.enabled; }
  setHidden(h: boolean) { this.hidden = h; }
  get visible() { return this.model.root.visible && this.enabled && !this.hidden; }

  /** Lão Đăng nói một câu (bong bóng chữ trên đầu). */
  say(text: string, ms = 5000) {
    if (!this.enabled || this.hidden) return;
    this.clearBubble();
    const b = speechSprite(text); b.position.y = 2.62;
    this.model.root.add(b); this.bubble = b; this.bubbleUntil = performance.now() + ms;
  }
  private clearBubble() { if (!this.bubble) return; this.bubble.removeFromParent(); this.bubble.material.map?.dispose(); this.bubble.material.dispose(); this.bubble = undefined; }

  /** Quay về hướng mục tiêu và giơ tay chỉ trong vài giây. */
  pointAt(x: number, z: number, sec = 3) { this.pointing = { x, z, until: performance.now() + sec * 1000 }; this.model.setAnim('point'); }

  placeNear(p: THREE.Vector3, rotY: number) {
    const s = this.slot(p, rotY, false);
    this.position.set(s.x, this.heightAt(s.x, s.z), s.z); this.rotY = rotY; this.velocity.set(0, 0, 0); this.sync();
  }

  /** Vị trí mong muốn: đi trước khi người chơi di chuyển, đứng chếch bên trái khi đứng yên. */
  private slot(p: THREE.Vector3, rotY: number, moving: boolean) {
    const fx = Math.sin(rotY), fz = Math.cos(rotY); const rx = -fz, rz = fx; // phải của nhân vật
    const ahead = moving ? 3.0 : 1.1, side = moving ? 1.0 : 1.3;
    return { x: p.x + fx * ahead - rx * side, z: p.z + fz * ahead - rz * side };
  }

  update(dt: number, player: { position: THREE.Vector3; rotY: number; speed: number }) {
    this.t += dt;
    const show = this.enabled && !this.hidden;
    this.fade += ((show ? 1 : 0) - this.fade) * Math.min(1, dt * 4);
    this.model.root.visible = this.fade > 0.05;
    // nhấp nháy tự nhiên + chập chờn mạnh khi gần vật ẩn
    const sense = show ? this.sense : 0;
    const flick = 0.88 + Math.sin(this.t * 11) * 0.05 + Math.sin(this.t * 27) * 0.04 + Math.sin(this.t * 3.1) * 0.03;
    const pulse = sense > 0 ? sense * (0.35 + 0.35 * Math.abs(Math.sin(this.t * (6 + sense * 14)))) : 0;
    const k = flick + pulse;
    this.body.update(this.t, k);
    this.light.intensity = show ? this.conf.intensity * k * this.fade : 0;
    if (this.bubble && performance.now() > this.bubbleUntil) this.clearBubble();
    if (!this.model.root.visible) { this.placeNear(player.position, player.rotY); return; }

    const moving = player.speed > 0.4;
    const s = this.slot(player.position, player.rotY, moving);
    if (Math.hypot(player.position.x - this.position.x, player.position.z - this.position.z) > 14) { this.placeNear(player.position, player.rotY); return; }
    const dx = s.x - this.position.x, dz = s.z - this.position.z; const dist = Math.hypot(dx, dz);
    const want = this.holdStill || dist < 0.25 ? 0 : Math.min(9, Math.max(player.speed * 1.05, dist * 2.2));
    const target = dist > 1e-3 ? new THREE.Vector3(dx / dist, 0, dz / dist).multiplyScalar(want) : new THREE.Vector3();
    this.velocity.lerp(target, 1 - Math.exp(-dt * 6));
    const nx = this.position.x + this.velocity.x * dt, nz = this.position.z + this.velocity.z * dt;
    const r = resolveCircle(nx, nz, 0.32, this.colliders);
    const gy = this.heightAt(r.x, r.z);
    const spd = Math.hypot(r.x - this.position.x, r.z - this.position.z) / Math.max(dt, 1e-4);
    this.position.set(r.x, this.position.y + (gy - this.position.y) * Math.min(1, dt * 14), r.z);
    // hướng: chỉ đường > đang bước > đứng cùng hướng người chơi (hoặc nhìn người chơi khi trò chuyện)
    const now = performance.now();
    if (this.pointing && now > this.pointing.until) this.pointing = null;
    const face = this.pointing ? Math.atan2(this.pointing.x - this.position.x, this.pointing.z - this.position.z)
      : this.holdStill ? Math.atan2(player.position.x - this.position.x, player.position.z - this.position.z)
        : spd > 0.6 ? Math.atan2(this.velocity.x, this.velocity.z) : player.rotY;
    let d = face - this.rotY; d = Math.atan2(Math.sin(d), Math.cos(d)); this.rotY += d * Math.min(1, dt * 6);
    if (!this.pointing) this.model.setAnim(this.holdStill ? 'talk' : spd > 5 ? 'run' : spd > 0.4 ? 'walk' : 'idle');
    this.sync();
    this.model.update(dt);
    // giữ đèn luôn thẳng đứng (treo theo trọng lực) + đung đưa nhẹ
    this.model.root.updateMatrixWorld(true);
    this.anchor.parent!.getWorldQuaternion(this.q1); this.model.root.getWorldQuaternion(this.q2);
    this.anchor.quaternion.copy(this.q1.invert().multiply(this.q2));
    const swing = Math.sin(this.t * (spd > 0.4 ? 7 : 1.6)) * (spd > 0.4 ? 0.12 : 0.04);
    this.anchor.quaternion.multiply(this.q2.setFromEuler(new THREE.Euler(swing, 0, swing * 0.5)));
  }

  private sync() { this.model.root.position.copy(this.position); this.model.root.rotation.y = this.rotY; }

  dispose() { this.clearBubble(); this.model.dispose(); }
}
